import { useState } from 'react';
import type { SyntheticEvent } from 'react';
import { useLocation } from 'react-router-dom';
import { Sidebar } from '@/components/layout/Sidebar';
import { useAuth } from '@/lib/auth';
import { INTAKE_PREFIX, intakeSrc, projectenUrl } from '@/lib/intake';

/**
 * Projecten: the intake app's pages (sb-facturatie-intake, repo sb-finance, served at /intake/*)
 * inside the platform chrome, at /alle-projecten (see lib/intake.ts for why not /projecten).
 * The sidebar stays the single navigation; the intake keeps its own styling and hides its
 * "Terug naar platform" bar when framed. Same origin: the gate cookie carries over and the
 * frame's location is readable. Deep link: /alle-projecten?p=/intake/ opens the form.
 * One scrollbar: the frame fills the content area and scrolls itself; the page does not.
 */
export default function Projecten() {
  const { user } = useAuth();
  const location = useLocation();
  const [blocked, setBlocked] = useState(false);
  const src = intakeSrc(location.search);

  // Mirror the frame's page into the address bar (?p=...) so reload and share keep the place.
  // history.replaceState keeps react-router's state, so this does not re-render or reload the frame.
  // A sidebar click is a new router location (new key) and remounts the frame on the list.
  const onLoad = (e: SyntheticEvent<HTMLIFrameElement>) => {
    try {
      const loc = e.currentTarget.contentWindow?.location;
      if (!loc || loc.href === 'about:blank') return;
      const path = loc.pathname + loc.search;
      if (!path.startsWith(INTAKE_PREFIX)) return;
      setBlocked(false);
      window.history.replaceState(window.history.state, '', projectenUrl(path));
    } catch {
      // Not readable = not our origin any more (e.g. an expired gate session sent the frame to a
      // login page that refuses framing). Offer the page outside the frame instead of a blank area.
      setBlocked(true);
    }
  };

  return (
    <div className="flex h-screen overflow-hidden bg-[#f6f6f7]">
      <Sidebar activeItem="projects" user={user} />

      <main id="main-content" className="flex min-w-0 flex-1 flex-col overflow-hidden pt-16 md:pt-0">
        {blocked && (
          <div className="border-b border-[#F3B2BE] bg-[#FCEDEE] px-6 py-3 text-sm text-[#121315]">
            De projectenlijst laadt niet binnen het platform.{' '}
            <a className="font-semibold text-[#E62E73] underline" href={src}>
              Open hem los
            </a>{' '}
            of log opnieuw in.
          </div>
        )}
        <iframe
          key={location.key}
          src={src}
          title="Projecten"
          onLoad={onLoad}
          className="block w-full flex-1 border-0"
        />
      </main>
    </div>
  );
}
